import { Feature } from '@/pinia/feature'

export const temperature: Feature = {
  name: 'temperature',
  enabled: true,
  title: {
    en: 'Temperature',
    sv: 'Temperatur'
  },
  info: {
    en: 'Temperatures are collected as averages over 10-minute intervals and aggregated over longer periods of time, then represented in degrees Celsius (°C).',
    sv: 'Temperaturer hämtas som medelvärden över 10-minutersintervall och aggregeras över längre tidsperioder för att sedan representeras i grader Celcius (°C).'
  },
  component: 'DashboardDataSectionFlat',
  isRoute: true,
  props: {
    dataSources: [
      {
        id: 'climate.temperature.kpi.0',
        type: 'fetch',
        fetch: 'fetch.indoorclimate.temperature.kpi',
        path: 'kpis'
      },
      {
        id: 'climate.temperature.mean.chart',
        type: 'fetch',
        fetch: 'fetch.indoorclimate.temperature.timeseries',
        path: 'timeSeries[0]'
      },
      {
        id: 'climate.temperature.histogram',
        type: 'fetch',
        fetch: 'fetch.indoorclimate.temperature.histogram',
        path: 'histograms[0].range'
      },
      {
        id: 'climate.temperature.toplist.average',
        type: 'fetch',
        fetch: 'fetch.indoorclimate.temperature.toplist.average',
        path: ''
      },
      {
        id: 'climate.temperature.colorscale',
        type: 'value',
        value: {
          type: 'INTERPOLATE',
          cutoffs: [
            {
              value: 17,
              color: '#0BA5EC'
            },
            {
              value: 19,
              color: '#36CFC9'
            },
            {
              value: 21,
              color: '#73D13D'
            },
            {
              value: 24,
              color: '#FFA940'
            },
            {
              value: 28,
              color: '#FF4D4F'
            }
          ]
        }
      }
    ]
  },
  children: [
    {
      name: 'temperature-kpi',
      enabled: true,
      title: {
        en: 'Mean value',
        sv: 'Medelvärde'
      },
      info: {
        en: 'Average over the selected time interval and chosen rooms.',
        sv: 'Medelvärde över valt tidsintervall och valda rum.'
      },
      component: 'KPICard',
      props: {
        colorScale: 'climate.temperature.colorscale',
        dataSource: 'climate.temperature.kpi.0',
        isLoading: false,
        showOnMap: false,
        showOnMapEnabled: true,
        first: true,
        kpiFormat: {
          format: {
            type: 'intl-number',
            style: 'unit',
            unit: 'celsius',
            maximumFractionDigits: 1
          }
        },
        metadataFormat: {
          space: {}
        },
        metadataLabels: {
          space: {
            en: 'Number of spaces',
            sv: 'Antal rum'
          }
        }
      }
    },
    {
      name: 'divider',
      title: {
        en: 'Divider',
        sv: 'Avdelare'
      },
      component: 'DividerComponent',
      enabled: true,
      props: {
        width: 'auto',
        fullscreenWidth: '70%'
      }
    },
    {
      name: 'temperature',
      enabled: true,
      title: {
        en: 'Change over time',
        sv: 'Förändring över tid'
      },
      info: {
        en: 'The graph visualizes the average indoor temperature over a given time period. The curve represents the varying temperatures and provides insight into how indoor temperature changes over time. This information is valuable for understanding and optimizing comfort levels, energy efficiency, and indoor well-being. \n\nThe gray areas in the graph indicate the absence of available data for certain periods or data that has been deliberately filtered out by the user (for example, weekends).',
        sv: 'Grafen visualiserar medeltemperaturen inomhus över en given tidsperiod. Kurvan representerar de varierande temperaturerna och ger en insikt i hur inomhustemperaturen förändras över tid. Denna information är värdefull för att förstå och optimera komfortnivåer, energieffektivitet och välbefinnande inomhus.  \n\nDe gråa områdena i grafen markerar avsaknaden av tillgänglig data under vissa tidsperioder eller data som medvetet har filtrerats bort av användaren (till exempel helger).'
      },
      component: 'CardChart',
      props: {
        enableFullscreen: true,
        chart: {
          type: 'timeseries',
          dataFormat: {
            format: {
              type: 'intl-number',
              style: 'unit',
              unit: 'celsius',
              maximumFractionDigits: 1
            }
          },
          labels: [
            {
              sv: 'Medeltemperatur',
              en: 'Average temperature'
            }
          ],
          lineFormats: [
            {
              style: 'solid'
            }
          ],
          colorScale: 'climate.temperature.colorscale',
          dataSources: ['climate.temperature.mean.chart'],
          missingDataMessage: {
            en: 'Missing data',
            sv: 'Saknar data'
          }
        }
      }
    },
    {
      name: 'temperature-hist',
      enabled: true,
      title: {
        en: 'Dispersion',
        sv: 'Spridning'
      },
      info: {
        en: 'This bar chart provides an overview of the indoor temperature distribution over a given time period.  \n\nEach bar represents different temperature ranges, and its height indicates the frequency of observations within each range. By analyzing the bar chart, one gets a clear picture of the distribution of indoor temperatures, which can be crucial for optimizing comfort and energy efficiency.',
        sv: 'Detta stapeldiagram ger en översikt över temperaturspridningen inomhus under en given tidsperiod.  \n\nVarje stapel representerar olika temperaturintervall och dess höjd indikerar frekvensen av observationer inom varje intervall. Genom att analysera stapeldiagrammet får man en tydlig bild av fördelningen av inomhustemperaturer, vilket kan vara avgörande för att optimera komfort och energieffektivitet.'
      },
      component: 'CardChart',
      props: {
        enableFullscreen: true,
        chart: {
          type: 'histogram',
          codomainFormat: {
            format: {
              type: 'intl-number',
              style: 'percent',
              maximumFractionDigits: 1
            }
          },
          domainFormat: {
            format: {
              type: 'intl-number',
              style: 'unit',
              unit: 'celsius',
              maximumFractionDigits: 1
            }
          },
          valueFilterTreshold: 0.001,
          codomainTicks: {
            values: [0, 0.5, 1]
          },
          missingDataMessage: {
            en: 'Missing data',
            sv: 'Saknar data'
          },
          label: {
            sv: 'Medeltemperatur',
            en: 'Average temperature'
          },
          colorScale: 'climate.temperature.colorscale',
          dataSource: 'climate.temperature.histogram'
        }
      }
    },
    {
      name: 'index-toplist',
      enabled: true,
      title: {
        en: 'Toplist',
        sv: 'Topplista'
      },
      component: 'CardTopList',
      props: {
        dataSource: 'climate.temperature.toplist.average',
        info: 'Some info about the toplist',
        format: {
          format: {
            type: 'intl-number',
            style: 'unit',
            unit: 'celsius',
            maximumFractionDigits: 1
          }
        },
        barOptions: [
          {
            label: {
              en: 'Temperature',
              sv: 'Temperatur'
            },
            color: '#0BA5EC',
            gradient: true,
            tooltip: true
          },
          {
            label: {
              en: 'Not measured',
              sv: 'Ej mätt'
            },
            color: '#E0E0E0',
            gradient: true,
            tooltip: false
          }
        ],
        errorMessages: {
          NO_DATA: {
            en: 'No data available',
            sv: 'Ingen data tillgänglig'
          },
          INVALID_FILTER: {
            en: 'Filter on complex to show data',
            sv: 'Filtrera på komplex för att visa data'
          }
        }
      }
    }
  ]
}
